import { CommonModule } from '@angular/common';
import { Component, ContentChildren, EventEmitter, Input, Output, QueryList, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { PlatformModule } from '@app/platform.module';
import { TranslatableKey } from '@app/types/translatable.type';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-checkbox-hierarchy',
    templateUrl: './checkbox-hierarchy.template.html',
    styleUrls: ['./checkbox-hierarchy.style.scss'],
    standalone: true,
    imports: [TranslateModule, CommonModule, MatSlideToggleModule, PlatformModule, FormsModule],
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CheckboxHierarchyComponent), multi: true }],
})
export class CheckboxHierarchyComponent implements ControlValueAccessor {
    /*
    NOTE: We cannot actually restrict the max levels via TS since the number of levels is determined via ng-content which is evaluated at runtime
    Instead a warning is given if you create a hierarchy beyond this level.
    If you wish to increase this level, you will also have to define additional styling within the .scss file
    */
    static readonly MAX_LEVEL = 2;
    static readonly MIN_LEVEL = 0;

    private static nextId = 0;

    // Will receive all immediate child checkboxes
    @ContentChildren(forwardRef(() => CheckboxHierarchyComponent), { descendants: false })
    set childCheckboxes(childCheckboxes: QueryList<CheckboxHierarchyComponent>) {
        // setTimeout is required here to avoid a content changed after it was checked error.
        // The content is still loading, therefore we need to add the parents in the following change detection cycle
        childCheckboxes.forEach((checkbox) => setTimeout(() => (checkbox.parent = this)));
        this._childCheckboxes = childCheckboxes;
    }

    get childCheckboxes(): QueryList<CheckboxHierarchyComponent> | undefined {
        return this._childCheckboxes;
    }
    @Input() id = `checkboxHierarchy-${(CheckboxHierarchyComponent.nextId += 1)}`;
    @Input() label!: TranslatableKey;
    @Input() required?: boolean;
    @Input() isLoading?: boolean;
    @Input() set disabled(isDisabled: boolean | undefined) {
        this._disabled = isDisabled;
    }
    get disabled(): boolean | undefined {
        // If the parent is disabled, the child will always be disabled. Otherwise, we rely on it's individual disabled state
        return this.parent?.disabled || this._disabled;
    }

    @Output() change: EventEmitter<boolean> = new EventEmitter<boolean>();

    parent?: CheckboxHierarchyComponent = undefined;
    onTouched?: (event: Event) => void;
    onChange?: (value: boolean) => void;
    private _checked = false;
    private _disabled? = false;
    private _childCheckboxes?: QueryList<CheckboxHierarchyComponent>;

    set checked(checked: boolean) {
        if (this.childCheckboxes?.length) {
            // Cascade changes to children
            this.childCheckboxes.forEach((checkbox) => {
                // Don't flip a child toggle if it is disabled
                if (!checkbox.disabled) {
                    checkbox.checked = checked;
                }
            });
        }
        this.change.emit(checked);
        this._checked = checked;
        this.onChange?.(checked);
    }

    get checked(): boolean {
        if (this.childCheckboxes?.length) {
            // A parent checkbox will flip itself if all of its children have also flipped
            const childrenValue = this.childCheckboxes.map((checkbox) => checkbox.checked).every(Boolean);
            if (childrenValue !== this._checked) {
                this._checked = childrenValue;
            }
        }
        return this._checked;
    }

    get level(): number {
        // If a checkbox has a parent, its own level should always be based off its parent
        if (this.parent) {
            return this.parent.level - 1;
        } else {
            // If a checkbox does not have a parent then there's two possibly scenarios
            // 1. It is a top level parent therefore it should be assigned the max level
            // 2. It is a singular checkbox therefore it should be assigned the minimum level
            return this.childCheckboxes?.length
                ? CheckboxHierarchyComponent.MAX_LEVEL
                : CheckboxHierarchyComponent.MIN_LEVEL;
        }
    }

    writeValue(value: boolean): void {
        this._checked = value;
    }

    registerOnChange(fn: (value: boolean) => void): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: (event: Event) => void): void {
        this.onTouched = fn;
    }
}
